import React, { ElementType, useState } from 'react';
import { formatCompact12HourTime, formatShortWeekdayMonthDay } from 'shared/Dates';
import { Grid, styled, useMediaQuery } from '@mui/material';
import { Colors, IconButton, Text, TextButton } from 'library';
import { ChevronLeft, ChevronRight, ExpandLess, ExpandMore } from '@mui/icons-material';
import theme from 'theme';
import { PermissionType, useUserContext } from 'UserContext';
import { AvailableTeacher } from 'models';

export default function Availability({
    teacher,
    requestToBook,
}: {
    teacher: AvailableTeacher;
    requestToBook: (teacher: AvailableTeacher, lower: string, upper: string, timezone?: string) => void;
}) {
    const xsDown = useMediaQuery(theme.breakpoints.down('sm'));
    const smDown = useMediaQuery(theme.breakpoints.down('md'));
    const slotsPerPage = xsDown ? 2 : smDown ? 3 : 4;
    const [slotsStart, setSlotsStart] = useState(0);
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);

    function ArrowButton({ icon, onClick, disabled }: { icon: ElementType; onClick: () => void; disabled: boolean }) {
        return (
            <IconButton
                disabled={disabled}
                icon={icon}
                onClick={onClick}
                color={Colors.white}
                backgroundColor={Colors.darkNavy}
                buttonStyle={{ width: 'auto' }}
            />
        );
    }

    return (
        <AllDaysContainer container small={xsDown}>
            <ArrowButton
                disabled={slotsStart <= 0}
                icon={xsDown ? ExpandLess : ChevronLeft}
                onClick={() => setSlotsStart(slotsStart - slotsPerPage)}
            />
            {teacher.sub_availability.slice(slotsStart, slotsStart + slotsPerPage).map((availability) => (
                <SlotContainer key={availability.id}>
                    <Header>
                        <Text variant="body2" bold>
                            {formatShortWeekdayMonthDay(availability.timeslot.lower)}
                        </Text>
                    </Header>
                    <Times>
                        <Slot>
                            <Text variant="body2">
                                {formatCompact12HourTime(availability.timeslot.lower, availability.timezone)}-
                                {formatCompact12HourTime(availability.timeslot.upper, availability.timezone)}
                            </Text>
                            {canPostJobs ? (
                                <TextButton
                                    onClick={() =>
                                        requestToBook(
                                            teacher,
                                            availability.timeslot.lower,
                                            availability.timeslot.upper,
                                            availability.timezone,
                                        )
                                    }
                                >
                                    Book
                                </TextButton>
                            ) : (
                                <></>
                            )}
                        </Slot>
                    </Times>
                </SlotContainer>
            ))}
            <ArrowButton
                disabled={slotsStart + slotsPerPage >= teacher.sub_availability.length}
                icon={xsDown ? ExpandMore : ChevronRight}
                onClick={() => setSlotsStart(slotsStart + slotsPerPage)}
            />
        </AllDaysContainer>
    );
}

const AllDaysContainer = styled(Grid)(({ small }: { small: boolean }) => ({
    gap: 10,
    padding: 10,
    alignItems: 'center',
    flexDirection: small ? 'column' : 'row',
}));

const SlotContainer = styled(Grid)({
    border: `1px solid ${Colors.lightGrey}`,
    borderRadius: 10,
    gap: 10,
});

const Header = styled(Grid)(() => ({
    backgroundColor: Colors.mediumTurq,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 10,
}));

const Times = styled(Grid)({
    padding: 10,
    borderRadius: 10,
});

const Slot = styled(Grid)({
    backgroundColor: Colors.lightTurq,
    borderRadius: 10,
    padding: 10,
});
